import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  LinearProgress,
  Skeleton,
  Tooltip,
  ButtonBase,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import Layout from '../components/Layout';
import FlashcardAssessment from '../components/FlashcardAssessment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionIcon from '@mui/icons-material/Description';
import SchoolIcon from '@mui/icons-material/School';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import BoltIcon from '@mui/icons-material/Bolt';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotesIcon from '@mui/icons-material/Notes';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import LinkIcon from '@mui/icons-material/Link';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LetterAIcon from '@mui/icons-material/TextFormat';
import Numbers123Icon from '@mui/icons-material/Numbers';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PdfExportPanel from '../components/PdfExportPanel';
import CatalogPicker from '../components/CatalogPicker';
import BookMaterialsPicker, {
  pluralRu,
} from '../components/BookMaterialsPicker';
import DashboardHero from '../components/dashboard/DashboardHero';
import StatTile from '../components/dashboard/StatTile';
import StudentCard from '../components/dashboard/StudentCard';
import {
  scrollToElement,
  usePressFeedback,
  useStaggerReveal,
} from '../components/fx/motion';
import { orchard } from '../theme';
import useStore from '../store/useStore';
import useIsMobile from '../hooks/useIsMobile';
import PersonPhotos from '../components/photos/PersonPhotos';
import BirthdayBoard from '../components/dashboard/BirthdayBoard';
import LearnCard from '../components/learn/LearnCard';
import CherryAdviceCard from '../components/dashboard/CherryAdviceCard';
import { translations } from '../translations';

// «1 ученик», «3 ученика», «6 учеников»
function ruStudentsCount(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ученик`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
    return `${n} ученика`;
  return `${n} учеников`;
}

// ── Skeleton loader ───────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <Grid container spacing={1.5}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
          <Skeleton variant="rounded" height={132} sx={{ borderRadius: 4 }} />
        </Grid>
      ))}
    </Grid>
  );
}

const chipSx = {
  flexShrink: 0,
  height: 34,
  borderRadius: 999,
  fontWeight: 700,
};

// ── Section heading with a count badge ────────────────────────────────────
function SectionTitle({ icon, color, label, count }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1.25,
        color,
        '& svg': { fontSize: 20 },
      }}
    >
      {icon}
      <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: 'inherit' }}>
        {label}
      </Typography>
      <Box
        component="span"
        sx={{
          px: 0.9,
          py: 0.1,
          borderRadius: 999,
          fontSize: '0.75rem',
          fontWeight: 800,
          bgcolor: 'action.hover',
          color: 'text.secondary',
        }}
      >
        {count}
      </Box>
    </Box>
  );
}

// ── "Needs attention" row: a label and tappable student pills ─────────────
function AttentionRow({ label, color, students, metric, onOpen }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: 800,
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {students.map((s) => (
          <ButtonBase
            key={s._id}
            onClick={() => onOpen(s._id)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 36,
              maxWidth: '100%',
              pl: 0.5,
              pr: 1.25,
              borderRadius: 999,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'background 0.18s ease',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Avatar
              src={s.photo || undefined}
              alt=""
              sx={{
                width: 26,
                height: 26,
                fontSize: '0.72rem',
                bgcolor: `${color}22`,
                color,
              }}
            >
              {s.name[0]}
            </Avatar>
            <Typography
              noWrap
              component="span"
              sx={{ fontSize: '0.82rem', fontWeight: 700 }}
            >
              {s.name}
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '0.78rem', fontWeight: 800, color }}
            >
              {metric(s)}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────
function EmptyState({ icon, title, text, actionLabel, onAction }) {
  return (
    <Box
      data-reveal
      sx={{
        textAlign: 'center',
        px: 3,
        py: { xs: 5, sm: 7 },
        borderRadius: '24px',
        border: '1.5px dashed',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'primary.100',
          color: 'primary.main',
          '& svg': { fontSize: 32 },
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
        {title}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{ mt: 0.5, fontSize: '0.9rem', maxWidth: 380, mx: 'auto' }}
      >
        {text}
      </Typography>
      {actionLabel && onAction && (
        <Button variant="outlined" onClick={onAction} sx={{ mt: 2 }}>
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function TeacherDashboard() {
  const isMobile = useIsMobile();
  const [dashData, setDashData] = useState(null);
  const [students, setStudents] = useState([]); // flat list for group mgmt
  const [groups, setGroups] = useState([]);
  const [pdfPanelOpen, setPdfPanelOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState(null); // placeholder student to merge away
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [merging, setMerging] = useState(false);
  const [activeGroup, setActiveGroup] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Group dialog
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    studentIds: [],
  });
  const [groupDialogTab, setGroupDialogTab] = useState(0);
  const [groupContent, setGroupContent] = useState([]);
  const [groupMeta, setGroupMeta] = useState({
    book: '',
    level: '',
    subject: '',
    notes: '',
  });
  const [groupThemeProgress, setGroupThemeProgress] = useState([]);
  const [groupSchedule, setGroupSchedule] = useState([]); // [{day, startTime, endTime}]
  const [groupTopics, setGroupTopics] = useState([]); // snapshots
  const [groupCurrentTopicId, setGroupCurrentTopicId] = useState(null);
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [newTopic, setNewTopic] = useState({
    name: '',
    lessonStart: 1,
    lessonEnd: 8,
    notes: '',
    clearActive: false,
  });
  const [newTheme, setNewTheme] = useState('');
  const [newItem, setNewItem] = useState({ type: 'word', text: '', url: '' });
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [bulkPasteType, setBulkPasteType] = useState('word');
  const [copyToDialogOpen, setCopyToDialogOpen] = useState(false);
  const [copyToTargetId, setCopyToTargetId] = useState('');
  const [copyToReplace, setCopyToReplace] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Quick assess modal
  const [qaStudent, setQaStudent] = useState(null);
  const [qaProfile, setQaProfile] = useState(null);
  const [qaLoading, setQaLoading] = useState(false);

  // Bulk group assess
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [bulkTemplate, setBulkTemplate] = useState(null);
  const [bulkSelectedThemes, setBulkSelectedThemes] = useState([]);
  const [bulkQueue, setBulkQueue] = useState([]);
  const [bulkCurrentIdx, setBulkCurrentIdx] = useState(0);
  const [bulkSpeedOpen, setBulkSpeedOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState([]); // [{ name, score, maxScore } | { name, skipped: true }]
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkFailed, setBulkFailed] = useState([]); // [{ name, message }] — could not prepare
  const [bulkPrepared, setBulkPrepared] = useState(null); // queue waiting for "continue without them"
  const [bulkStudentIds, setBulkStudentIds] = useState([]); // students ticked in the setup dialog

  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [bookPickerOpen, setBookPickerOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const pageRef = useRef(null);
  const overdueRef = useRef(null);
  useStaggerReveal(pageRef);
  usePressFeedback(pageRef);

  const { user, language } = useStore();

  // ── Restore bulk session from sessionStorage on mount ──────────────────────
  const BULK_SESSION_KEY = 'bulkAssessSession';
  const BULK_TEMPLATE_KEY = 'bulkAssessLastTemplate';
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(BULK_SESSION_KEY));
      if (
        saved &&
        saved.queue?.length > 0 &&
        saved.currentIdx < saved.queue.length
      ) {
        const resume = window.confirm(
          language === 'en'
            ? `Resume the unfinished group assessment? (${saved.currentIdx} of ${saved.queue.length} students done)`
            : `Продолжить незавершённую оценку группы? (Готово ${saved.currentIdx} из ${saved.queue.length} учеников)`
        );
        if (resume) {
          setBulkQueue(saved.queue);
          setBulkCurrentIdx(saved.currentIdx);
          setBulkResults(saved.results || []);
          setBulkFailed(saved.failed || []);
          setBulkSelectedThemes(saved.selectedThemes || []);
          setBulkSpeedOpen(true);
        } else {
          sessionStorage.removeItem(BULK_SESSION_KEY);
        }
      }
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const t = translations[language];
  const navigate = useNavigate();

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch('/api/analytics/dashboard', { credentials: 'include' }).then((r) =>
        r.json()
      ),
      fetch('/api/students', { credentials: 'include' }).then((r) => r.json()),
      fetch('/api/auth/groups', { credentials: 'include' }).then((r) =>
        r.json()
      ),
    ])
      .then(([dash, studs, grps]) => {
        setDashData(dash);
        setStudents(Array.isArray(studs) ? studs : []);
        setGroups(Array.isArray(grps) ? grps : []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const fetchGroups = () =>
    fetch('/api/auth/groups', { credentials: 'include' })
      .then((r) => r.json())
      .then(setGroups);

  const openMergeDialog = (student) => {
    setMergeSource(student);
    setMergeTargetId('');
  };

  const doMerge = async () => {
    if (!mergeSource || !mergeTargetId) return;
    setMerging(true);
    try {
      const res = await fetch(
        `/api/students/${mergeSource._id}/merge/${mergeTargetId}`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Merge failed: ${res.status}`);
      }
      setSnack({
        open: true,
        severity: 'success',
        message:
          language === 'en'
            ? 'Student data merged'
            : 'Данные ученика объединены',
      });
      setMergeSource(null);
      setMergeTargetId('');
      fetchDashboard();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e.message });
    } finally {
      setMerging(false);
    }
  };

  // ── Quick Assess: fetch profile first, then open speed mode ──────────────
  const handleQuickAssess = async (student) => {
    if (!student.latestProfileId) {
      alert(
        language === 'en'
          ? 'No assessment found for this student'
          : 'Нет оценки для этого ученика'
      );
      return;
    }
    setQaLoading(true);
    try {
      const res = await fetch(`/api/profiles/${student.latestProfileId}`, {
        credentials: 'include',
      });
      const profile = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          profile.message ||
            (language === 'en'
              ? 'Could not load the assessment'
              : 'Не удалось загрузить оценку')
        );
      }
      setQaProfile(profile);
      setQaStudent(student);
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e.message });
    } finally {
      setQaLoading(false);
    }
  };

  // Students of the active group, normalised to { _id, name }
  const getActiveGroupStudents = () => {
    const g = groups.find((gr) => gr._id === activeGroup);
    return (g?.studentIds || []).map((s) =>
      typeof s === 'object'
        ? { _id: s._id.toString(), name: s.name || '?' }
        : {
            _id: s.toString(),
            name: students.find((st) => st._id === s)?.name || '?',
          }
    );
  };

  // ── Bulk Assess: open dialog and load templates ───────────────────────────
  const handleOpenBulk = async () => {
    let list;
    try {
      const res = await fetch('/api/templates', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.message ||
            (language === 'en'
              ? 'Could not load templates'
              : 'Не удалось загрузить шаблоны')
        );
      }
      list = Array.isArray(data) ? data : [];
      setTemplates(list);
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e.message });
      return;
    }
    // Preselect the template used last time (or the only one), all themes on
    let preset = null;
    try {
      const lastId = localStorage.getItem(BULK_TEMPLATE_KEY);
      preset = list.find((tm) => tm._id === lastId) || null;
    } catch (_) {}
    if (!preset && list.length === 1) preset = list[0];
    setBulkTemplateId(preset?._id || '');
    setBulkTemplate(preset);
    setBulkSelectedThemes(preset ? preset.themes.map((th) => th.name) : []);
    setBulkStudentIds(getActiveGroupStudents().map((s) => s._id));
    setBulkQueue([]);
    setBulkCurrentIdx(0);
    setBulkSpeedOpen(false);
    setBulkResults([]);
    setBulkFailed([]);
    setBulkPrepared(null);
    setBulkDone(false);
    setBulkOpen(true);
  };

  const saveBulkSession = (queue, currentIdx, results, failed) => {
    try {
      sessionStorage.setItem(
        BULK_SESSION_KEY,
        JSON.stringify({
          queue,
          currentIdx,
          results,
          failed,
          selectedThemes: bulkSelectedThemes,
        })
      );
    } catch (_) {}
  };

  const launchBulkSession = (queue, failed) => {
    setBulkPrepared(null);
    setBulkQueue(queue);
    setBulkCurrentIdx(0);
    setBulkResults([]);
    setBulkDone(false);
    setBulkOpen(false);
    saveBulkSession(queue, 0, [], failed);
    setBulkSpeedOpen(true);
  };

  // Changing the template/themes invalidates an already-prepared queue
  const resetBulkPreparation = () => {
    setBulkPrepared(null);
    setBulkFailed([]);
  };

  // ── Bulk Assess: start session — fetch/create profiles for each student ──
  const handleBulkStart = async () => {
    const groupStudents = getActiveGroupStudents().filter((s) =>
      bulkStudentIds.includes(s._id)
    );
    if (groupStudents.length === 0) {
      setSnack({
        open: true,
        severity: 'error',
        message:
          language === 'en' ? 'No students selected' : 'Не выбраны ученики',
      });
      return;
    }
    try {
      localStorage.setItem(BULK_TEMPLATE_KEY, bulkTemplateId);
    } catch (_) {}

    setBulkLoading(true);
    setBulkPrepared(null);
    setBulkFailed([]);
    const assessedThemeIds = (bulkTemplate?.themes || [])
      .filter((th) => bulkSelectedThemes.includes(th.name))
      .map((th) => th._id);
    const profileName = `Оценка ${new Date().toLocaleDateString('ru-RU')}`;

    try {
      const prepared = await Promise.all(
        groupStudents.map(async (student) => {
          const sid = (student._id || student).toString();
          const name = student.name || '?';
          try {
            const res = await fetch(
              `/api/profiles/student/${sid}?templateId=${bulkTemplateId}`,
              { credentials: 'include' }
            );
            const forTemplate = await res.json().catch(() => null);
            if (!res.ok) {
              throw new Error(forTemplate?.message || `HTTP ${res.status}`);
            }
            const latest =
              Array.isArray(forTemplate) && forTemplate.length > 0
                ? forTemplate[0]
                : null;

            if (latest) {
              // Grow the existing assessment with chosen units it lacks
              // (Unit 1 → Unit 1 + Unit 2) instead of starting another one
              const have = latest.themes || [];
              const missingIds = (bulkTemplate?.themes || [])
                .filter(
                  (th) =>
                    assessedThemeIds.includes(th._id) &&
                    !have.some(
                      (pt) =>
                        String(pt.themeId) === String(th._id) ||
                        pt.themeName === th.name
                    )
                )
                .map((th) => th._id);
              if (missingIds.length && latest.isLatest !== false) {
                const extRes = await fetch(
                  `/api/profiles/${latest._id}/themes`,
                  {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ themeIds: missingIds }),
                  }
                );
                const ext = await extRes.json().catch(() => null);
                if (!extRes.ok || !Array.isArray(ext?.profile?.themes)) {
                  throw new Error(ext?.message || `HTTP ${extRes.status}`);
                }
                return { student: { _id: sid, name }, profile: ext.profile };
              }
              return { student: { _id: sid, name }, profile: latest };
            }

            // Create blank profile from template (server builds the themes)
            const createRes = await fetch(`/api/profiles/student/${sid}`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                templateId: bulkTemplateId,
                profileName,
                assessmentType: 'initial',
                assessedThemeIds,
              }),
            });
            const newProfile = await createRes.json().catch(() => null);
            if (!createRes.ok || !Array.isArray(newProfile?.themes)) {
              throw new Error(
                newProfile?.message || `HTTP ${createRes.status}`
              );
            }
            return { student: { _id: sid, name }, profile: newProfile };
          } catch (e) {
            return {
              student: { _id: sid, name },
              error:
                e instanceof TypeError
                  ? language === 'en'
                    ? 'Network error'
                    : 'Ошибка сети'
                  : e.message,
            };
          }
        })
      );

      const valid = prepared.filter((p) => !p.error);
      const failed = prepared
        .filter((p) => p.error)
        .map((p) => ({ name: p.student.name, message: p.error }));

      if (failed.length > 0) {
        // Keep the dialog open and say who failed; the teacher can retry or
        // continue with the students that did load
        setBulkFailed(failed);
        setBulkPrepared(valid);
        return;
      }
      launchBulkSession(valid, []);
    } finally {
      setBulkLoading(false);
    }
  };

  const finishBulkSession = () => {
    sessionStorage.removeItem(BULK_SESSION_KEY);
    setBulkSpeedOpen(false);
    setBulkDone(true);
    fetchDashboard();
  };

  const advanceBulk = (newResults, endSession) => {
    setBulkResults(newResults);
    const nextIdx = bulkCurrentIdx + 1;
    if (nextIdx < bulkQueue.length && !endSession) {
      saveBulkSession(bulkQueue, nextIdx, newResults, bulkFailed);
      setBulkCurrentIdx(nextIdx);
    } else {
      finishBulkSession();
    }
  };

  // Student saved → next student (or end, when asked or after the last one)
  const handleBulkSave = (newThemes, opts = {}) => {
    const student = bulkQueue[bulkCurrentIdx]?.student;
    // Count only the themes this session assessed (all, if none match)
    const sessionThemes = newThemes.filter((th) =>
      bulkSelectedThemes.includes(th.themeName)
    );
    const scored = (sessionThemes.length > 0 ? sessionThemes : newThemes)
      .flatMap((th) => th.items || [])
      .map((i) => i.score || 0);
    const known = scored.filter((s) => s === 2).length;
    const partial = scored.filter((s) => s === 1).length;
    advanceBulk(
      [
        ...bulkResults,
        {
          studentId: student?._id,
          name: student?.name,
          known,
          partial,
          unknown: scored.length - known - partial,
          score: scored.reduce((a, s) => a + s, 0),
          maxScore: scored.length * 2,
        },
      ],
      opts.endSession
    );
  };

  // Student absent → record as skipped, nothing saved
  const handleBulkSkip = () => {
    const student = bulkQueue[bulkCurrentIdx]?.student;
    advanceBulk(
      [
        ...bulkResults,
        { studentId: student?._id, name: student?.name, skipped: true },
      ],
      false
    );
  };

  // Explicit close/cancel (confirmed inside the speed-mode dialog)
  const handleBulkClose = () => {
    finishBulkSession();
  };

  const handleBulkSummaryClose = () => {
    setBulkDone(false);
    setBulkResults([]);
    setBulkFailed([]);
    setBulkQueue([]);
    setBulkCurrentIdx(0);
  };

  const handleSaveGroup = async () => {
    const url = editingGroup
      ? `/api/auth/groups/${editingGroup._id}`
      : '/api/auth/groups';
    const method = editingGroup ? 'PATCH' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...groupFormData,
        content: groupContent,
        ...groupMeta,
        themeProgress: groupThemeProgress,
        schedule: groupSchedule,
      }),
      credentials: 'include',
    });
    if (res.ok) {
      setGroups(await res.json());
      setOpenGroupDialog(false);
      setEditingGroup(null);
      setGroupFormData({ name: '', studentIds: [] });
      setGroupContent([]);
      setGroupMeta({ book: '', level: '', subject: '', notes: '' });
      setGroupThemeProgress([]);
      setGroupSchedule([]);
      setGroupTopics([]);
      setGroupCurrentTopicId(null);
      setGroupDialogTab(0);
      setSnack({
        open: true,
        message: editingGroup ? 'Группа сохранена' : 'Группа создана',
        severity: 'success',
      });
    } else {
      setSnack({ open: true, message: 'Ошибка сохранения', severity: 'error' });
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (
      !window.confirm(
        language === 'en' ? 'Delete this group?' : 'Удалить эту группу?'
      )
    )
      return;
    const res = await fetch(`/api/auth/groups/${groupId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok) {
      setGroups(await res.json());
      if (activeGroup === groupId) setActiveGroup('all');
    }
  };

  const handleCloneGroup = async (groupId) => {
    const res = await fetch(`/api/auth/groups/${groupId}/clone`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) {
      setGroups(await res.json());
      setSnack({
        open: true,
        message: 'Группа скопирована',
        severity: 'success',
      });
    } else {
      setSnack({
        open: true,
        message: 'Ошибка копирования',
        severity: 'error',
      });
    }
  };

  const refreshGroupsAndReopen = async (groupsJson) => {
    setGroups(groupsJson);
    if (editingGroup) {
      const updated = groupsJson.find((g) => g._id === editingGroup._id);
      if (updated) {
        setGroupTopics(
          updated.topics ? updated.topics.map((t) => ({ ...t })) : []
        );
        setGroupContent(
          updated.content ? updated.content.map((c) => ({ ...c })) : []
        );
        setGroupCurrentTopicId(updated.currentTopicId || null);
        setEditingGroup(updated);
      }
    }
  };

  const handleSaveTopic = async () => {
    if (!editingGroup || !newTopic.name.trim()) return;
    // Persist current group state first so content snapshot is fresh
    await fetch(`/api/auth/groups/${editingGroup._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: groupContent }),
      credentials: 'include',
    });
    const res = await fetch(`/api/auth/groups/${editingGroup._id}/topics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newTopic),
      credentials: 'include',
    });
    if (res.ok) {
      await refreshGroupsAndReopen(await res.json());
      setTopicDialogOpen(false);
      setNewTopic({
        name: '',
        lessonStart: 1,
        lessonEnd: 8,
        notes: '',
        clearActive: false,
      });
      setSnack({
        open: true,
        message: `Тема «${newTopic.name}» сохранена`,
        severity: 'success',
      });
    } else {
      setSnack({
        open: true,
        message: 'Ошибка сохранения темы',
        severity: 'error',
      });
    }
  };

  const handleLoadTopic = async (topicId, mode = 'replace') => {
    if (!editingGroup) return;
    if (mode === 'replace' && groupContent.length > 0) {
      if (
        !window.confirm(
          'Заменить текущие материалы на эту тему? Убедитесь, что сохранили текущий набор как тему, иначе он будет перезаписан.'
        )
      )
        return;
    }
    const res = await fetch(
      `/api/auth/groups/${editingGroup._id}/topics/${topicId}/load`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
        credentials: 'include',
      }
    );
    if (res.ok) {
      await refreshGroupsAndReopen(await res.json());
      setSnack({ open: true, message: 'Тема загружена', severity: 'success' });
    } else {
      setSnack({
        open: true,
        message: 'Ошибка загрузки темы',
        severity: 'error',
      });
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!editingGroup) return;
    if (
      !window.confirm('Удалить эту тему навсегда? Содержимое не восстановить.')
    )
      return;
    const res = await fetch(
      `/api/auth/groups/${editingGroup._id}/topics/${topicId}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );
    if (res.ok) {
      await refreshGroupsAndReopen(await res.json());
      setSnack({ open: true, message: 'Тема удалена', severity: 'success' });
    }
  };

  const handleCopyContentTo = async (srcId, targetId, replace) => {
    const res = await fetch(
      `/api/auth/groups/${srcId}/copy-content-to/${targetId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ replace }),
        credentials: 'include',
      }
    );
    if (res.ok) {
      setGroups(await res.json());
      setCopyToDialogOpen(false);
      setSnack({
        open: true,
        message: replace ? 'Материалы заменены' : 'Материалы скопированы',
        severity: 'success',
      });
    } else {
      setSnack({
        open: true,
        message: 'Ошибка копирования материалов',
        severity: 'error',
      });
    }
  };

  const openEdit = (group) => {
    setEditingGroup(group);
    setGroupFormData({
      name: group.name,
      studentIds: group.studentIds.map((s) => s._id || s),
    });
    setGroupContent(group.content ? group.content.map((c) => ({ ...c })) : []);
    setGroupMeta({
      book: group.book || '',
      level: group.level || '',
      subject: group.subject || '',
      notes: group.notes || '',
    });
    setGroupThemeProgress(
      group.themeProgress ? group.themeProgress.map((t) => ({ ...t })) : []
    );
    setGroupSchedule(
      group.schedule ? group.schedule.map((s) => ({ ...s })) : []
    );
    setGroupTopics(group.topics ? group.topics.map((t) => ({ ...t })) : []);
    setGroupCurrentTopicId(group.currentTopicId || null);
    setGroupDialogTab(0);
    setNewItem({ type: 'word', text: '', url: '' });
    setNewTheme('');
    setShowBulkPaste(false);
    setBulkPasteText('');
    setBulkPasteType('word');
    setOpenGroupDialog(true);
  };

  // «Из книги»: the dialog edits a local draft, so the picked items join it
  // and are stored with «Сохранить группу». The picker already skips items
  // the group has.
  const addFromBook = async (items) => {
    setGroupContent((prev) => [...prev, ...items]);
    setSnack({
      open: true,
      message:
        language === 'en'
          ? `Added ${items.length} ${items.length === 1 ? 'item' : 'items'} — press “Save group” to keep them`
          : `Добавлено ${items.length} ${pluralRu(items.length, ['материал', 'материала', 'материалов'])} — нажмите «Сохранить группу»`,
      severity: 'success',
    });
  };

  // «Из каталога»: homework packs / maths pages from the material library.
  const addFromCatalog = (items) => {
    setGroupContent((prev) => [...prev, ...items]);
    setSnack({
      open: true,
      message:
        language === 'en'
          ? `Added ${items.length} ${items.length === 1 ? 'file' : 'files'} — press “Save group” to keep them`
          : `Добавлено ${items.length} ${pluralRu(items.length, ['файл', 'файла', 'файлов'])} — нажмите «Сохранить группу»`,
      severity: 'success',
    });
  };

  // «Очистить всё»: empties the draft; saved topics stay untouched.
  const clearGroupContent = () => {
    const n = groupContent.length;
    setGroupContent([]);
    setClearConfirmOpen(false);
    setSnack({
      open: true,
      message:
        language === 'en'
          ? `Removed ${n} items — press “Save group” to confirm`
          : `Убрано ${n} ${pluralRu(n, ['материал', 'материала', 'материалов'])} — нажмите «Сохранить группу», чтобы сохранить`,
      severity: 'info',
    });
  };

  // Book + its units as themes; themes the group already has keep their
  // lessonsCompleted, new units go to the end.
  const applyBookDetails = async (details) => {
    const known = new Set(
      groupThemeProgress.map((tp) =>
        String(tp.theme || '')
          .trim()
          .toLowerCase()
      )
    );
    const added = [];
    (details.themeProgress || []).forEach((tp) => {
      const key = String(tp.theme || '')
        .trim()
        .toLowerCase();
      if (!key || known.has(key)) return;
      known.add(key);
      added.push(tp);
    });
    const book = details.book || groupMeta.book;
    const level = details.level || groupMeta.level;
    setGroupMeta((m) => ({ ...m, book, level }));
    setGroupThemeProgress((prev) => [...prev, ...added]);
    setSnack({
      open: true,
      message:
        language === 'en'
          ? `Book “${book}” set${added.length ? `, ${added.length} themes added` : ''} — press “Save group” to keep it`
          : `Учебник «${book}»${added.length ? `, добавлено тем: ${added.length}` : ' установлен'} — нажмите «Сохранить группу»`,
      severity: 'success',
    });
  };

  // Students to display: merge dashboard data with group filter
  const allDashStudents = dashData?.students || [];
  const groupFilteredIds =
    activeGroup === 'all'
      ? null
      : (() => {
          const g = groups.find((g) => g._id === activeGroup);
          return new Set(
            g?.studentIds.map((s) => (s._id || s).toString()) || []
          );
        })();

  const displayedStudents = allDashStudents.filter((s) => {
    if (groupFilteredIds && !groupFilteredIds.has(s._id.toString()))
      return false;
    if (
      searchQuery &&
      !s.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    if (activeTagFilter && !(s.tags || []).includes(activeTagFilter))
      return false;
    return true;
  });

  const overdueStudents = displayedStudents.filter(
    (s) => s.daysSinceAssessed === null || s.daysSinceAssessed >= 30
  );
  const recentStudents = displayedStudents.filter(
    (s) => s.daysSinceAssessed !== null && s.daysSinceAssessed < 30
  );

  // Needs Attention derived lists
  const decliningStudents = allDashStudents.filter(
    (s) => s.masteryTrend === 'down'
  );
  const lowAttendanceStudents = allDashStudents.filter(
    (s) =>
      s.attendanceRate !== null &&
      s.attendanceRate !== undefined &&
      s.attendanceRate < 70
  );

  // All tags for filter bar
  const allTags = [
    ...new Set(allDashStudents.flatMap((s) => s.tags || [])),
  ].sort();

  const stats = dashData?.stats;

  // Bulk setup dialog
  const bulkGroupStudents = getActiveGroupStudents();
  const bulkChosenCount = bulkGroupStudents.filter((s) =>
    bulkStudentIds.includes(s._id)
  ).length;

  // ── Presentation helpers ───────────────────────────────────────────────────
  const en = language === 'en';
  const filtersActive = Boolean(searchQuery.trim()) || Boolean(activeTagFilter);
  const resetFilters = () => {
    setSearchQuery('');
    setActiveTagFilter(null);
  };

  const groupNamesByStudent = new Map();
  groups.forEach((g) =>
    (g.studentIds || []).forEach((s) => {
      const id = String(s._id || s);
      if (!groupNamesByStudent.has(id)) groupNamesByStudent.set(id, []);
      groupNamesByStudent.get(id).push(g.name);
    })
  );

  const overdueCount = overdueStudents.length;
  const todayLabel = new Date().toLocaleDateString(en ? 'en-GB' : 'ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const heroSubtitle = loading
    ? ' '
    : overdueCount > 0
      ? en
        ? `${overdueCount} ${overdueCount === 1 ? 'student is' : 'students are'} waiting for an assessment`
        : `Ждут оценки: ${ruStudentsCount(overdueCount)}`
      : en
        ? 'All assessments are up to date'
        : 'Все оценки актуальны';

  const renderStudent = (s, i) => (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s._id}>
      <StudentCard
        student={s}
        groupNames={groupNamesByStudent.get(String(s._id)) || []}
        onNavigate={(id) => navigate(`/student/${id}`)}
        onQuickAssess={handleQuickAssess}
        onTagClick={(tag) =>
          setActiveTagFilter(activeTagFilter === tag ? null : tag)
        }
        onMerge={openMergeDialog}
        activeTagFilter={activeTagFilter}
        en={en}
        index={i}
      />
    </Grid>
  );

  return (
    <Layout title={t.teacher}>
      <Container maxWidth="lg" ref={pageRef}>
        {/* ── Hero: greeting, today's focus, key numbers ── */}
        <DashboardHero
          eyebrow={todayLabel}
          title={`${t.welcome}, ${user?.displayName || user?.username}!`}
          subtitle={heroSubtitle}
          avatar={
            <PersonPhotos
              kind="teacher"
              id="me"
              name={user?.displayName || user?.username}
              canEdit
              size={isMobile ? 56 : 76}
            />
          }
          actions={
            !loading && overdueCount > 0 ? (
              <Button
                variant="contained"
                disableElevation
                endIcon={<ArrowDownwardIcon />}
                onClick={() => scrollToElement(overdueRef.current)}
                sx={{
                  bgcolor: '#fff',
                  // theme gives contained buttons a cherry gradient image
                  backgroundImage: 'none',
                  color: orchard.cherryDeep,
                  fontWeight: 800,
                  '&:hover': {
                    bgcolor: orchard.blossom,
                    backgroundImage: 'none',
                  },
                }}
              >
                {en ? 'Show who is due' : 'Показать, кого оценить'}
              </Button>
            ) : null
          }
        >
          {loading && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: { xs: 1, sm: 1.5 },
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={64}
                  sx={{
                    borderRadius: '16px',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }}
                />
              ))}
            </Box>
          )}
          {stats && !loading && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: { xs: 1, sm: 1.5 },
              }}
            >
              <StatTile
                glass
                icon={<PeopleIcon />}
                label={en ? 'Students' : 'Ученики'}
                value={stats.totalStudents}
                color={orchard.cherryBright}
              />
              <StatTile
                glass
                icon={<CheckCircleIcon />}
                label={en ? 'Assessed this week' : 'Оценено за неделю'}
                value={stats.assessedThisWeek}
                subtitle={
                  en ? `of ${stats.totalStudents}` : `из ${stats.totalStudents}`
                }
                color={orchard.leafBright}
                delay={0.1}
              />
              <StatTile
                glass
                icon={<WarningAmberIcon />}
                label={en ? 'Due for reassessment' : 'Пора переоценить'}
                value={stats.dueForReassessment}
                subtitle={en ? '30+ days inactive' : '30+ дней без оценки'}
                color={orchard.honey}
                delay={0.2}
              />
              <StatTile
                glass
                icon={<EmojiEventsIcon />}
                label={en ? 'Class mastery' : 'Освоение класса'}
                value={Number(stats.classAvgMastery) || 0}
                suffix="%"
                subtitle={en ? 'avg of assessed' : 'среднее по оценённым'}
                color={orchard.honey}
                delay={0.3}
              />
            </Box>
          )}
        </DashboardHero>

        <LearnCard />

        {loading ? (
          <DashboardSkeleton />
        ) : (
          <>
            <CherryAdviceCard en={en} />
            <BirthdayBoard en={en} />

            {/* ── Needs attention ── */}
            {(decliningStudents.length > 0 ||
              lowAttendanceStudents.length > 0) && (
              <Box
                data-reveal
                sx={(theme) => ({
                  mb: 2.5,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '18px',
                  border: '1px solid',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(232,169,61,0.35)'
                      : 'rgba(232,169,61,0.5)',
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(232,169,61,0.08)'
                      : 'rgba(232,169,61,0.09)',
                })}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <WarningAmberIcon
                    sx={{ color: 'warning.main', fontSize: 20 }}
                  />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                    {en ? 'Needs attention' : 'Требуют внимания'}
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {decliningStudents.length > 0 && (
                    <AttentionRow
                      label={
                        en ? '↓ Declining mastery' : '↓ Снижение успеваемости'
                      }
                      color={orchard.cherry}
                      students={decliningStudents}
                      metric={(s) => `${s.avgMastery}%`}
                      onOpen={(id) => navigate(`/student/${id}`)}
                    />
                  )}
                  {lowAttendanceStudents.length > 0 && (
                    <AttentionRow
                      label={
                        en
                          ? '⚠ Low attendance (<70%)'
                          : '⚠ Низкая посещаемость (<70%)'
                      }
                      color="#B7791F"
                      students={lowAttendanceStudents}
                      metric={(s) => `${s.attendanceRate}%`}
                      onOpen={(id) => navigate(`/student/${id}`)}
                    />
                  )}
                </Stack>
              </Box>
            )}

            {/* ── Search, groups, tags ── */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mb: 1.5,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                placeholder={en ? 'Search students…' : 'Найти ученика…'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label={en ? 'Clear search' : 'Очистить поиск'}
                          onClick={() => setSearchQuery('')}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
                sx={{
                  flex: '1 1 220px',
                  maxWidth: { sm: 360 },
                  '& input': { fontSize: { xs: 16, sm: 14 } },
                  '& .MuiOutlinedInput-root': { borderRadius: 999 },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingGroup(null);
                    setGroupFormData({ name: '', studentIds: [] });
                    setGroupContent([]);
                    setGroupDialogTab(0);
                    setNewItem({ type: 'word', text: '', url: '' });
                    setOpenGroupDialog(true);
                  }}
                  sx={{ minHeight: 36 }}
                >
                  {en ? 'New group' : 'Новая группа'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={() => setPdfPanelOpen(true)}
                  sx={{ minHeight: 36 }}
                >
                  {en ? 'Download all' : 'Скачать всё'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate('/analytics?tab=recent')}
                  sx={{ minHeight: 36 }}
                >
                  {en ? 'Recently assessed' : 'Недавно оценённые'}
                </Button>
              </Box>
            </Box>

            {groups.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    overflowX: 'auto',
                    pb: 0.5,
                    mx: -0.5,
                    px: 0.5,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  <Chip
                    label={en ? 'All' : 'Все'}
                    icon={<FilterListIcon />}
                    onClick={() => setActiveGroup('all')}
                    color={activeGroup === 'all' ? 'primary' : 'default'}
                    variant={activeGroup === 'all' ? 'filled' : 'outlined'}
                    sx={chipSx}
                  />
                  {groups.map((g) => (
                    <Chip
                      key={g._id}
                      label={`${g.name} · ${(g.studentIds || []).length}`}
                      onClick={() => setActiveGroup(g._id)}
                      onDelete={
                        activeGroup === g._id ? () => openEdit(g) : undefined
                      }
                      deleteIcon={<EditIcon />}
                      color={activeGroup === g._id ? 'primary' : 'default'}
                      variant={activeGroup === g._id ? 'filled' : 'outlined'}
                      sx={chipSx}
                    />
                  ))}
                </Stack>
                {activeGroup !== 'all' && (
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<BoltIcon />}
                    onClick={handleOpenBulk}
                    disabled={qaLoading}
                    sx={{ mt: 1, minHeight: 40 }}
                  >
                    {en
                      ? `Assess group: ${groups.find((g) => g._id === activeGroup)?.name || ''}`
                      : `Оценить группу: ${groups.find((g) => g._id === activeGroup)?.name || ''}`}
                  </Button>
                )}
              </Box>
            )}

            {allTags.length > 0 && (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{
                  mb: 2,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {allTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={`#${tag}`}
                    size="small"
                    onClick={() =>
                      setActiveTagFilter(activeTagFilter === tag ? null : tag)
                    }
                    color={activeTagFilter === tag ? 'primary' : 'default'}
                    variant={activeTagFilter === tag ? 'filled' : 'outlined'}
                    sx={{ flexShrink: 0, borderRadius: 999 }}
                  />
                ))}
              </Stack>
            )}

            {/* ── Due for reassessment ── */}
            {overdueStudents.length > 0 && (
              <Box ref={overdueRef} sx={{ mb: 3 }}>
                <SectionTitle
                  icon={<WarningAmberIcon />}
                  color="error.main"
                  label={en ? 'Due for reassessment' : 'Пора переоценить'}
                  count={overdueStudents.length}
                />
                <Grid container spacing={1.5}>
                  {overdueStudents.map((s, i) => renderStudent(s, i))}
                </Grid>
              </Box>
            )}

            {/* ── All / recent students ── */}
            <Box>
              {recentStudents.length > 0 && overdueStudents.length > 0 && (
                <SectionTitle
                  icon={<SchoolIcon />}
                  color="text.secondary"
                  label={en ? 'Recently assessed' : 'Недавно оценены'}
                  count={recentStudents.length}
                />
              )}

              {displayedStudents.length === 0 ? (
                <EmptyState
                  icon={filtersActive ? <SearchOffIcon /> : <SchoolIcon />}
                  title={
                    allDashStudents.length === 0
                      ? en
                        ? 'No students assigned yet'
                        : 'Пока нет назначенных учеников'
                      : filtersActive
                        ? en
                          ? 'Nobody matches these filters'
                          : 'Никого не нашли по этим фильтрам'
                        : en
                          ? 'No students in this group'
                          : 'В этой группе пока нет учеников'
                  }
                  text={
                    allDashStudents.length === 0
                      ? en
                        ? 'Students will show up here once the administrator assigns them to you.'
                        : 'Ученики появятся здесь, когда администратор закрепит их за вами.'
                      : filtersActive
                        ? en
                          ? 'Try a different name or clear the filters.'
                          : 'Попробуйте другое имя или сбросьте фильтры.'
                        : en
                          ? 'Add students to the group with the pencil on its chip.'
                          : 'Добавьте учеников в группу — карандаш на её метке выше.'
                  }
                  actionLabel={
                    filtersActive
                      ? en
                        ? 'Reset filters'
                        : 'Сбросить фильтры'
                      : activeGroup !== 'all'
                        ? en
                          ? 'Edit group'
                          : 'Изменить группу'
                        : undefined
                  }
                  onAction={
                    filtersActive
                      ? resetFilters
                      : activeGroup !== 'all'
                        ? () =>
                            openEdit(groups.find((g) => g._id === activeGroup))
                        : undefined
                  }
                />
              ) : (
                <Grid container spacing={1.5}>
                  {(overdueStudents.length > 0
                    ? recentStudents
                    : displayedStudents
                  ).map((s, i) => renderStudent(s, i))}
                </Grid>
              )}
            </Box>

            {/* ── Quick links ── */}
            <Box
              sx={{
                mt: 4,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<AnalyticsIcon />}
                onClick={() => navigate('/analytics')}
                sx={{ borderRadius: '16px', py: 1.5 }}
              >
                {en ? 'Analytics' : 'Аналитика'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={() => navigate('/templates')}
                sx={{ borderRadius: '16px', py: 1.5 }}
              >
                {en ? 'Templates' : 'Шаблоны'}
              </Button>
            </Box>
          </>
        )}

        {/* ── Group dialog ── */}
        <Dialog
          open={openGroupDialog}
          onClose={() => setOpenGroupDialog(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {editingGroup
              ? `${en ? 'Edit' : 'Группа'}: ${editingGroup.name}`
              : en
                ? 'Create new group'
                : 'Новая группа'}
          </DialogTitle>
          <Tabs
            value={groupDialogTab}
            onChange={(_, v) => setGroupDialogTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Tab label="Ученики" />
            <Tab
              label={
                groupContent.length > 0
                  ? `Материалы (${groupContent.length})`
                  : 'Материалы'
              }
            />
            <Tab
              label={
                groupMeta.book ||
                groupMeta.level ||
                groupThemeProgress.length > 0
                  ? 'Подробности ✓'
                  : 'Подробности'
              }
            />
          </Tabs>
          <DialogContent>
            {/* ── Tab 0: Members ── */}
            {groupDialogTab === 0 && (
              <Box
                sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}
              >
                <TextField
                  label={en ? 'Group name' : 'Название группы'}
                  fullWidth
                  placeholder={
                    en
                      ? 'e.g. Age 3, Sunday 15:35'
                      : 'напр. 3 года, воскресенье 15:35'
                  }
                  value={groupFormData.name}
                  onChange={(e) =>
                    setGroupFormData({ ...groupFormData, name: e.target.value })
                  }
                />
                <FormControl fullWidth>
                  <InputLabel>
                    {en ? 'Select students' : 'Ученики группы'}
                  </InputLabel>
                  <Select
                    multiple
                    value={groupFormData.studentIds}
                    label={en ? 'Select students' : 'Ученики группы'}
                    onChange={(e) =>
                      setGroupFormData({
                        ...groupFormData,
                        studentIds: e.target.value,
                      })
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const student = students.find((s) => s._id === value);
                          return (
                            <Chip
                              key={value}
                              label={student?.name}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {students.map((student) => (
                      <MenuItem key={student._id} value={student._id}>
                        {student.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* ── Tab 1: Content ── */}
            {groupDialogTab === 1 && (
              <Box sx={{ mt: 1 }}>
                {/* ── Topic slider (snapshots per 8-lesson set) ── */}
                {editingGroup && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: '1px dashed',
                      borderColor: 'primary.200',
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <BookmarkIcon
                        sx={{ fontSize: 18, color: 'primary.main' }}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ flexGrow: 1 }}
                      >
                        Темы
                        {groupTopics.length > 0 && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            ({groupTopics.length})
                          </Typography>
                        )}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon fontSize="small" />}
                        onClick={() => setTopicDialogOpen(true)}
                        disabled={groupContent.length === 0}
                      >
                        Сохранить как тему
                      </Button>
                    </Stack>
                    {groupTopics.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Темы ещё не сохранены. Когда закончите набор из 8 уроков
                        — сохраните тему, чтобы в любой момент вернуться к этим
                        материалам.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          overflowX: 'auto',
                          pb: 0.5,
                          '&::-webkit-scrollbar': { height: 6 },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'grey.300',
                            borderRadius: 3,
                          },
                        }}
                      >
                        {groupTopics.map((topic) => {
                          const isActive =
                            String(topic._id) === String(groupCurrentTopicId);
                          return (
                            <Paper
                              key={topic._id}
                              elevation={0}
                              onClick={() =>
                                handleLoadTopic(topic._id, 'replace')
                              }
                              sx={{
                                minWidth: 160,
                                maxWidth: 200,
                                p: 1,
                                border: '2px solid',
                                borderColor: isActive
                                  ? 'primary.main'
                                  : 'grey.200',
                                bgcolor: isActive ? 'primary.50' : 'white',
                                borderRadius: 2,
                                cursor: 'pointer',
                                flexShrink: 0,
                                '&:hover': {
                                  borderColor: 'primary.400',
                                  bgcolor: 'primary.50',
                                },
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                spacing={0.5}
                              >
                                {isActive ? (
                                  <BookmarkIcon
                                    sx={{ fontSize: 14, color: 'primary.main' }}
                                  />
                                ) : (
                                  <BookmarkBorderIcon
                                    sx={{ fontSize: 14, color: 'grey.500' }}
                                  />
                                )}
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    noWrap
                                  >
                                    {topic.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                  >
                                    Уроки {topic.lessonStart}–{topic.lessonEnd}{' '}
                                    · {topic.content?.length || 0}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTopic(topic._id);
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <DeleteIcon
                                    sx={{ fontSize: 14, color: 'error.light' }}
                                  />
                                </IconButton>
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ mt: 0.5 }}
                              >
                                <Tooltip title="Добавить к текущим">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLoadTopic(topic._id, 'append');
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <AddIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Загрузить (заменить)">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLoadTopic(topic._id, 'replace');
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <RestoreIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
                {/* Toolbar: bulk, export, import, clone, copy-to */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<MenuBookIcon />}
                    onClick={() => setBookPickerOpen(true)}
                  >
                    {en ? 'From a book' : 'Из книги'}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disableElevation
                    startIcon={<FolderOpenIcon />}
                    onClick={() => setCatalogOpen(true)}
                  >
                    {en ? 'From the catalogue' : 'Из каталога'}
                  </Button>
                  <Button
                    size="small"
                    variant={showBulkPaste ? 'contained' : 'outlined'}
                    onClick={() => setShowBulkPaste((v) => !v)}
                  >
                    Массово
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const blob = new Blob(
                        [JSON.stringify(groupContent, null, 2)],
                        { type: 'application/json' }
                      );
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `materials-${groupFormData.name || 'group'}.json`;
                      a.click();
                    }}
                    disabled={groupContent.length === 0}
                  >
                    Экспорт
                  </Button>
                  <Button size="small" variant="outlined" component="label">
                    Импорт
                    <input
                      type="file"
                      accept=".json"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          try {
                            const imported = JSON.parse(ev.target.result);
                            if (Array.isArray(imported)) {
                              setGroupContent((prev) => [...prev, ...imported]);
                              setSnack({
                                open: true,
                                message: `Импортировано ${imported.length} элементов`,
                                severity: 'success',
                              });
                            }
                          } catch {
                            setSnack({
                              open: true,
                              message: 'Ошибка импорта JSON',
                              severity: 'error',
                            });
                          }
                        };
                        reader.readAsText(file);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  {editingGroup && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleCloneGroup(editingGroup._id)}
                      >
                        Клонировать группу
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={groupContent.length === 0}
                        onClick={() => {
                          setCopyToTargetId('');
                          setCopyToReplace(false);
                          setCopyToDialogOpen(true);
                        }}
                      >
                        Копировать материалы в...
                      </Button>
                    </>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    disabled={groupContent.length === 0}
                    onClick={() => setClearConfirmOpen(true)}
                  >
                    {en ? 'Clear all' : 'Очистить всё'}
                  </Button>
                </Stack>

                {/* Bulk paste section */}
                {showBulkPaste && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'primary.200',
                      borderRadius: 2,
                      bgcolor: 'primary.50',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Тип</InputLabel>
                        <Select
                          value={bulkPasteType}
                          label="Тип"
                          onChange={(e) => setBulkPasteType(e.target.value)}
                        >
                          <MenuItem value="word">Слово</MenuItem>
                          <MenuItem value="phrase">Фраза</MenuItem>
                          <MenuItem value="letter">Буква</MenuItem>
                          <MenuItem value="number">Число</MenuItem>
                          <MenuItem value="youtube">YouTube</MenuItem>
                          <MenuItem value="link">Ссылка</MenuItem>
                          <MenuItem value="craft">Активность</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexGrow: 1 }}
                      >
                        Вставьте несколько записей (каждая с новой строки или
                        через запятую)
                      </Typography>
                    </Stack>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="apple, banana, cat&#10;dog&#10;elephant"
                      value={bulkPasteText}
                      onChange={(e) => setBulkPasteText(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!bulkPasteText.trim()}
                      onClick={() => {
                        const lines = bulkPasteText
                          .split(/[\n,]/)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const newItems = lines.map((text) => ({
                          type: bulkPasteType,
                          text,
                          url: '',
                        }));
                        setGroupContent((prev) => [...prev, ...newItems]);
                        setBulkPasteText('');
                        setSnack({
                          open: true,
                          message: `Добавлено ${newItems.length} элементов`,
                          severity: 'success',
                        });
                      }}
                    >
                      Добавить{' '}
                      {
                        bulkPasteText.split(/[\n,]/).filter((s) => s.trim())
                          .length
                      }{' '}
                      элементов
                    </Button>
                  </Box>
                )}

                {/* Add single item row */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2 }}
                  alignItems="flex-start"
                >
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Тип</InputLabel>
                    <Select
                      value={newItem.type}
                      label="Тип"
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          type: e.target.value,
                          url: '',
                        })
                      }
                    >
                      <MenuItem value="word">Слово</MenuItem>
                      <MenuItem value="phrase">Фраза</MenuItem>
                      <MenuItem value="letter">Буква</MenuItem>
                      <MenuItem value="number">Число</MenuItem>
                      <MenuItem value="youtube">YouTube</MenuItem>
                      <MenuItem value="link">Ссылка</MenuItem>
                      <MenuItem value="craft">Активность</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder={
                      newItem.type === 'youtube'
                        ? 'Название (необязательно)'
                        : newItem.type === 'letter'
                          ? 'Буква(ы), напр. A, B, C'
                          : newItem.type === 'phrase'
                            ? 'Напр. How are you?'
                            : newItem.type === 'craft'
                              ? 'Напр. Бумажная бабочка'
                              : newItem.type === 'number'
                                ? 'Напр. 1, 2, 3'
                                : newItem.type === 'link'
                                  ? 'Название ссылки'
                                  : 'Напр. apple'
                    }
                    value={newItem.text}
                    onChange={(e) =>
                      setNewItem({ ...newItem, text: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const text = newItem.text.trim();
                        const url = newItem.url.trim();
                        if (!text && !url) return;
                        setGroupContent([
                          ...groupContent,
                          { type: newItem.type, text, url },
                        ]);
                        setNewItem({ ...newItem, text: '', url: '' });
                      }
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  {(newItem.type === 'youtube' || newItem.type === 'link') && (
                    <TextField
                      size="small"
                      placeholder={
                        newItem.type === 'youtube'
                          ? 'YouTube URL'
                          : 'https://...'
                      }
                      value={newItem.url}
                      onChange={(e) =>
                        setNewItem({ ...newItem, url: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const text = newItem.text.trim();
                          const url = newItem.url.trim();
                          if (!text && !url) return;
                          setGroupContent([
                            ...groupContent,
                            { type: newItem.type, text, url },
                          ]);
                          setNewItem({ ...newItem, text: '', url: '' });
                        }
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                  )}
                  <IconButton
                    color="primary"
                    onClick={() => {
                      const text = newItem.text.trim();
                      const url = newItem.url.trim();
                      if (!text && !url) return;
                      setGroupContent([
                        ...groupContent,
                        { type: newItem.type, text, url },
                      ]);
                      setNewItem({ ...newItem, text: '', url: '' });
                    }}
                    sx={{
                      bgcolor: 'primary.50',
                      '&:hover': { bgcolor: 'primary.100' },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Stack>

                <Divider sx={{ mb: 1.5 }} />

                {groupContent.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2, textAlign: 'center' }}
                  >
                    Нет материалов. Добавьте слова, фразы, YouTube-ссылки или
                    другие активности выше.
                  </Typography>
                ) : (
                  (() => {
                    const typeMeta = {
                      word: {
                        label: 'Слова',
                        color: '#C2233E',
                        Icon: TextFieldsIcon,
                      },
                      phrase: {
                        label: 'Фразы',
                        color: '#0891b2',
                        Icon: ChatBubbleOutlineIcon,
                      },
                      video: {
                        label: 'Видео',
                        color: '#dc2626',
                        Icon: PlayCircleIcon,
                      },
                      youtube: {
                        label: 'Видео',
                        color: '#dc2626',
                        Icon: PlayCircleIcon,
                      },
                      link: {
                        label: 'Ссылки',
                        color: '#0369a1',
                        Icon: LinkIcon,
                      },
                      file: {
                        label: 'Файлы и задания',
                        color: '#2E7D5B',
                        Icon: DescriptionIcon,
                      },
                      craft: {
                        label: 'Активности',
                        color: '#d97706',
                        Icon: ColorLensIcon,
                      },
                      letter: {
                        label: 'Буквы',
                        color: '#16a34a',
                        Icon: LetterAIcon,
                      },
                      number: {
                        label: 'Числа',
                        color: '#7c3aed',
                        Icon: Numbers123Icon,
                      },
                    };
                    const order = [
                      'word',
                      'phrase',
                      'video',
                      'youtube',
                      'file',
                      'link',
                      'craft',
                      'letter',
                      'number',
                    ];
                    const buckets = {};
                    groupContent.forEach((it, origIdx) => {
                      const key = it.type === 'youtube' ? 'video' : it.type;
                      if (!buckets[key]) buckets[key] = [];
                      buckets[key].push({ ...it, _origIdx: origIdx });
                    });
                    const rendered = [];
                    for (const key of order) {
                      if (!buckets[key]) continue;
                      const meta = typeMeta[key] || typeMeta.word;
                      const Icon = meta.Icon;
                      rendered.push(
                        <Box
                          key={key}
                          sx={{
                            mb: 1.5,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: `${meta.color}33`,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              bgcolor: `${meta.color}14`,
                            }}
                          >
                            <Icon sx={{ fontSize: 16, color: meta.color }} />
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              sx={{
                                color: meta.color,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                flexGrow: 1,
                              }}
                            >
                              {meta.label}
                            </Typography>
                            <Chip
                              label={buckets[key].length}
                              size="small"
                              sx={{
                                height: 18,
                                bgcolor: 'white',
                                color: meta.color,
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                          <Box sx={{ p: 0.5 }}>
                            {buckets[key].map((item) => (
                              <Box
                                key={item._origIdx}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: 'grey.50' },
                                }}
                              >
                                <TextField
                                  size="small"
                                  variant="standard"
                                  value={item.text}
                                  placeholder={
                                    key === 'video' || key === 'link'
                                      ? 'Название'
                                      : 'Текст'
                                  }
                                  onChange={(e) =>
                                    setGroupContent((prev) =>
                                      prev.map((x, j) =>
                                        j === item._origIdx
                                          ? { ...x, text: e.target.value }
                                          : x
                                      )
                                    )
                                  }
                                  sx={{
                                    flexGrow: 1,
                                    '& .MuiInputBase-input': {
                                      fontWeight: 500,
                                      fontSize: '0.88rem',
                                    },
                                  }}
                                  InputProps={{ disableUnderline: true }}
                                />
                                {(key === 'video' || key === 'link') && (
                                  <TextField
                                    size="small"
                                    variant="standard"
                                    value={item.url || ''}
                                    placeholder="https://..."
                                    onChange={(e) =>
                                      setGroupContent((prev) =>
                                        prev.map((x, j) =>
                                          j === item._origIdx
                                            ? { ...x, url: e.target.value }
                                            : x
                                        )
                                      )
                                    }
                                    sx={{
                                      flex: '1 1 260px',
                                      '& .MuiInputBase-input': {
                                        fontSize: '0.78rem',
                                        color: meta.color,
                                        fontFamily: 'monospace',
                                      },
                                    }}
                                    InputProps={{
                                      disableUnderline: true,
                                      endAdornment: item.url ? (
                                        <Tooltip title="Открыть">
                                          <IconButton
                                            size="small"
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{ p: 0.25 }}
                                          >
                                            <LinkIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      ) : null,
                                    }}
                                  />
                                )}
                                {key === 'file' && item.url && (
                                  <Tooltip title={item.note || 'Открыть'}>
                                    <IconButton
                                      size="small"
                                      href={item.url}
                                      target="_blank"
                                      rel="noopener"
                                      sx={{ p: 0.25, color: meta.color }}
                                    >
                                      <OpenInNewIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setGroupContent((prev) =>
                                      prev.filter((_, j) => j !== item._origIdx)
                                    )
                                  }
                                  sx={{ p: 0.25 }}
                                >
                                  <DeleteIcon
                                    sx={{ fontSize: 14, color: 'error.light' }}
                                  />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                        {rendered}
                      </Box>
                    );
                  })()
                )}
              </Box>
            )}
            {/* ── Tab 2: Details (metadata + theme progress) ── */}
            {groupDialogTab === 2 && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1.5 }}
                >
                  Информация о группе
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      label="Учебник"
                      fullWidth
                      value={groupMeta.book}
                      onChange={(e) =>
                        setGroupMeta((m) => ({ ...m, book: e.target.value }))
                      }
                      InputProps={{
                        startAdornment: (
                          <MenuBookIcon
                            sx={{
                              mr: 1,
                              fontSize: 18,
                              color: 'text.secondary',
                            }}
                          />
                        ),
                      }}
                    />
                    <TextField
                      size="small"
                      label="Уровень (A1, B2...)"
                      value={groupMeta.level}
                      onChange={(e) =>
                        setGroupMeta((m) => ({ ...m, level: e.target.value }))
                      }
                      sx={{ minWidth: 160 }}
                      InputProps={{
                        startAdornment: (
                          <SchoolIcon
                            sx={{
                              mr: 1,
                              fontSize: 18,
                              color: 'text.secondary',
                            }}
                          />
                        ),
                      }}
                    />
                    <TextField
                      size="small"
                      label="Предмет"
                      value={groupMeta.subject}
                      onChange={(e) =>
                        setGroupMeta((m) => ({ ...m, subject: e.target.value }))
                      }
                      sx={{ minWidth: 140 }}
                    />
                  </Stack>
                  <TextField
                    size="small"
                    label="Заметки"
                    fullWidth
                    multiline
                    rows={2}
                    value={groupMeta.notes}
                    onChange={(e) =>
                      setGroupMeta((m) => ({ ...m, notes: e.target.value }))
                    }
                    InputProps={{
                      startAdornment: (
                        <NotesIcon
                          sx={{
                            mr: 1,
                            mt: 0.5,
                            fontSize: 18,
                            color: 'text.secondary',
                            alignSelf: 'flex-start',
                          }}
                        />
                      ),
                    }}
                  />
                </Stack>

                <Divider sx={{ mb: 1.5 }} />
                {/* ── Schedule ── */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <ScheduleIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ flexGrow: 1 }}
                  >
                    Расписание
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={() =>
                      setGroupSchedule((prev) => [
                        ...prev,
                        { day: 'mon', startTime: '15:30', endTime: '16:30' },
                      ])
                    }
                  >
                    Добавить слот
                  </Button>
                </Stack>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {groupSchedule.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Не указано. Добавьте дни и время занятий.
                    </Typography>
                  )}
                  {groupSchedule.map((slot, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>День</InputLabel>
                        <Select
                          value={slot.day}
                          label="День"
                          onChange={(e) =>
                            setGroupSchedule((prev) =>
                              prev.map((s, j) =>
                                j === i ? { ...s, day: e.target.value } : s
                              )
                            )
                          }
                        >
                          <MenuItem value="mon">Понедельник</MenuItem>
                          <MenuItem value="tue">Вторник</MenuItem>
                          <MenuItem value="wed">Среда</MenuItem>
                          <MenuItem value="thu">Четверг</MenuItem>
                          <MenuItem value="fri">Пятница</MenuItem>
                          <MenuItem value="sat">Суббота</MenuItem>
                          <MenuItem value="sun">Воскресенье</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="time"
                        label="Начало"
                        value={slot.startTime}
                        onChange={(e) =>
                          setGroupSchedule((prev) =>
                            prev.map((s, j) =>
                              j === i ? { ...s, startTime: e.target.value } : s
                            )
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 110 }}
                      />
                      <TextField
                        size="small"
                        type="time"
                        label="Конец"
                        value={slot.endTime}
                        onChange={(e) =>
                          setGroupSchedule((prev) =>
                            prev.map((s, j) =>
                              j === i ? { ...s, endTime: e.target.value } : s
                            )
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 110 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setGroupSchedule((prev) =>
                            prev.filter((_, j) => j !== i)
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Прогресс по темам (уроки / 8)
                </Typography>
                <Stack
                  spacing={1.5}
                  sx={{ mb: 1.5, maxHeight: 220, overflowY: 'auto' }}
                >
                  {groupThemeProgress.map((tp, i) => (
                    <Box key={i}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ flexGrow: 1 }}
                        >
                          {tp.theme}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 36, textAlign: 'right' }}
                        >
                          {tp.lessonsCompleted}/8
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setGroupThemeProgress((prev) =>
                              prev.map((t, j) =>
                                j === i
                                  ? {
                                      ...t,
                                      lessonsCompleted: Math.max(
                                        0,
                                        t.lessonsCompleted - 1
                                      ),
                                    }
                                  : t
                              )
                            )
                          }
                          disabled={tp.lessonsCompleted === 0}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setGroupThemeProgress((prev) =>
                              prev.map((t, j) =>
                                j === i
                                  ? {
                                      ...t,
                                      lessonsCompleted: Math.min(
                                        8,
                                        t.lessonsCompleted + 1
                                      ),
                                    }
                                  : t
                              )
                            )
                          }
                          disabled={tp.lessonsCompleted === 8}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setGroupThemeProgress((prev) =>
                              prev.filter((_, j) => j !== i)
                            )
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(tp.lessonsCompleted / 8) * 100}
                        sx={{ borderRadius: 1, height: 6 }}
                        color={
                          tp.lessonsCompleted === 8 ? 'success' : 'primary'
                        }
                      />
                    </Box>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Новая тема..."
                    value={newTheme}
                    onChange={(e) => setNewTheme(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newTheme.trim()) {
                        setGroupThemeProgress((prev) => [
                          ...prev,
                          { theme: newTheme.trim(), lessonsCompleted: 0 },
                        ]);
                        setNewTheme('');
                      }
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={!newTheme.trim()}
                    onClick={() => {
                      setGroupThemeProgress((prev) => [
                        ...prev,
                        { theme: newTheme.trim(), lessonsCompleted: 0 },
                      ]);
                      setNewTheme('');
                    }}
                  >
                    Добавить
                  </Button>
                </Stack>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {editingGroup && (
              <Button
                color="error"
                onClick={() => {
                  handleDeleteGroup(editingGroup._id);
                  setOpenGroupDialog(false);
                }}
              >
                {en ? 'Delete group' : 'Удалить группу'}
              </Button>
            )}
            <Button onClick={() => setOpenGroupDialog(false)}>
              {en ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveGroup}
              disabled={!groupFormData.name}
            >
              {en ? 'Save group' : 'Сохранить группу'}
            </Button>
          </DialogActions>
        </Dialog>

        <BookMaterialsPicker
          open={bookPickerOpen}
          onClose={() => setBookPickerOpen(false)}
          existingItems={groupContent}
          onAdd={addFromBook}
          onApplyDetails={applyBookDetails}
        />

        <CatalogPicker
          open={catalogOpen}
          onClose={() => setCatalogOpen(false)}
          existingItems={groupContent}
          onAdd={addFromCatalog}
        />

        <Dialog
          open={clearConfirmOpen}
          onClose={() => setClearConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {en ? 'Clear all materials?' : 'Очистить все материалы?'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {en
                ? `All ${groupContent.length} items will be removed from this group's current materials. Saved topics stay. Nothing changes until you press “Save group”.`
                : `Из текущих материалов группы будут убраны все ${groupContent.length} ${pluralRu(groupContent.length, ['элемент', 'элемента', 'элементов'])}. Сохранённые темы останутся. Изменение вступит в силу после «Сохранить группу».`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClearConfirmOpen(false)}>
              {en ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              color="error"
              variant="contained"
              disableElevation
              onClick={clearGroupContent}
            >
              {en ? 'Clear' : 'Очистить'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Copy materials to dialog ── */}
        <Dialog
          open={copyToDialogOpen}
          onClose={() => setCopyToDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Копировать материалы в группу
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Целевая группа</InputLabel>
              <Select
                value={copyToTargetId}
                label="Целевая группа"
                onChange={(e) => setCopyToTargetId(e.target.value)}
              >
                {groups
                  .filter((g) => g._id !== editingGroup?._id)
                  .map((g) => (
                    <MenuItem key={g._id} value={g._id}>
                      {g.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ mt: 1.5 }}
              control={
                <Checkbox
                  checked={copyToReplace}
                  onChange={(e) => setCopyToReplace(e.target.checked)}
                />
              }
              label="Заменить существующие материалы (иначе добавить)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCopyToDialogOpen(false)}>Отмена</Button>
            <Button
              variant="contained"
              disabled={!copyToTargetId}
              onClick={() =>
                handleCopyContentTo(
                  editingGroup._id,
                  copyToTargetId,
                  copyToReplace
                )
              }
            >
              Копировать
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Save-as-Topic dialog ── */}
        <Dialog
          open={topicDialogOpen}
          onClose={() => setTopicDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Сохранить как тему</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                size="small"
                label="Название темы"
                placeholder="напр. My Family, Colours, Farm Animals"
                fullWidth
                value={newTopic.name}
                onChange={(e) =>
                  setNewTopic((p) => ({ ...p, name: e.target.value }))
                }
                autoFocus
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  label="Урок с"
                  value={newTopic.lessonStart}
                  onChange={(e) =>
                    setNewTopic((p) => ({
                      ...p,
                      lessonStart: Number(e.target.value) || 1,
                    }))
                  }
                  inputProps={{ min: 1, max: 200 }}
                  sx={{ width: 100 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="по"
                  value={newTopic.lessonEnd}
                  onChange={(e) =>
                    setNewTopic((p) => ({
                      ...p,
                      lessonEnd: Number(e.target.value) || 1,
                    }))
                  }
                  inputProps={{ min: 1, max: 200 }}
                  sx={{ width: 100 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ alignSelf: 'center' }}
                >
                  Обычно 8 уроков на тему
                </Typography>
              </Stack>
              <TextField
                size="small"
                label="Заметки (необязательно)"
                fullWidth
                multiline
                rows={2}
                value={newTopic.notes}
                onChange={(e) =>
                  setNewTopic((p) => ({ ...p, notes: e.target.value }))
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newTopic.clearActive}
                    onChange={(e) =>
                      setNewTopic((p) => ({
                        ...p,
                        clearActive: e.target.checked,
                      }))
                    }
                  />
                }
                label="Очистить активные материалы после сохранения (начать новую тему)"
              />
              <Alert severity="info" sx={{ py: 0.5 }}>
                Снимок из {groupContent.length} элементов. Доступ в любое время
                — клик по теме в слайдере.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTopicDialogOpen(false)}>Отмена</Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!newTopic.name.trim()}
              onClick={handleSaveTopic}
            >
              Сохранить тему
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Quick Assess modal ── */}
        {qaStudent && qaProfile && (
          <FlashcardAssessment
            open={!!qaStudent}
            onClose={() => {
              setQaStudent(null);
              setQaProfile(null);
            }}
            profile={qaProfile}
            profileId={qaProfile._id}
            onSave={() => {
              setQaStudent(null);
              setQaProfile(null);
              fetchDashboard();
            }}
          />
        )}

        {/* ── Bulk Assess: setup dialog ── */}
        <Dialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            {language === 'en'
              ? `Assess group: ${groups.find((g) => g._id === activeGroup)?.name || ''}`
              : `Оценка группы: ${groups.find((g) => g._id === activeGroup)?.name || ''}`}
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            {/* Students — all ticked; untick whoever is missing */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, color: 'text.secondary' }}
              >
                {language === 'en' ? 'Students' : 'Ученики'} · {bulkChosenCount}
                /{bulkGroupStudents.length}
              </Typography>
              {bulkGroupStudents.length > 0 && (
                <Button
                  size="small"
                  onClick={() => {
                    setBulkStudentIds(
                      bulkChosenCount === bulkGroupStudents.length
                        ? []
                        : bulkGroupStudents.map((s) => s._id)
                    );
                    resetBulkPreparation();
                  }}
                >
                  {bulkChosenCount === bulkGroupStudents.length
                    ? language === 'en'
                      ? 'Clear all'
                      : 'Снять все'
                    : language === 'en'
                      ? 'Select all'
                      : 'Выбрать всех'}
                </Button>
              )}
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
                columnGap: 1,
                maxHeight: 200,
                overflowY: 'auto',
                mb: 2,
              }}
            >
              {bulkGroupStudents.map((s) => (
                <FormControlLabel
                  key={s._id}
                  sx={{
                    mr: 0,
                    minWidth: 0,
                    '& .MuiFormControlLabel-label': { minWidth: 0 },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      sx={{ py: 0.5 }}
                      checked={bulkStudentIds.includes(s._id)}
                      onChange={(e) => {
                        setBulkStudentIds((prev) =>
                          e.target.checked
                            ? [...prev, s._id]
                            : prev.filter((id) => id !== s._id)
                        );
                        resetBulkPreparation();
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" noWrap>
                      {s.name}
                    </Typography>
                  }
                />
              ))}
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel>
                {language === 'en' ? 'Template' : 'Шаблон'}
              </InputLabel>
              <Select
                value={bulkTemplateId}
                label={language === 'en' ? 'Template' : 'Шаблон'}
                onChange={(e) => {
                  const id = e.target.value;
                  setBulkTemplateId(id);
                  const tmpl = templates.find((t) => t._id === id) || null;
                  setBulkTemplate(tmpl);
                  setBulkSelectedThemes(
                    tmpl ? tmpl.themes.map((t) => t.name) : []
                  );
                  resetBulkPreparation();
                }}
              >
                {templates.map((t) => (
                  <MenuItem key={t._id} value={t._id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Themes as toggle chips — all on by default */}
            {bulkTemplate && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color: 'text.secondary' }}
                >
                  {language === 'en' ? 'Themes' : 'Темы'} ·{' '}
                  {bulkSelectedThemes.length}/{bulkTemplate.themes.length}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={0.75}
                >
                  {(() => {
                    const allOn =
                      bulkSelectedThemes.length === bulkTemplate.themes.length;
                    return (
                      <Chip
                        label={language === 'en' ? 'All' : 'Все'}
                        size="small"
                        color={allOn ? 'primary' : 'default'}
                        variant={allOn ? 'filled' : 'outlined'}
                        aria-pressed={allOn}
                        onClick={() => {
                          setBulkSelectedThemes(
                            allOn ? [] : bulkTemplate.themes.map((t) => t.name)
                          );
                          resetBulkPreparation();
                        }}
                        sx={{ fontWeight: 700 }}
                      />
                    );
                  })()}
                  {bulkTemplate.themes.map((theme, idx) => {
                    const on = bulkSelectedThemes.includes(theme.name);
                    return (
                      <Chip
                        key={theme._id || idx}
                        label={`${theme.name} · ${(theme.items || []).length}`}
                        size="small"
                        color={on ? 'primary' : 'default'}
                        variant={on ? 'filled' : 'outlined'}
                        aria-pressed={on}
                        onClick={() => {
                          setBulkSelectedThemes((prev) =>
                            on
                              ? prev.filter((n) => n !== theme.name)
                              : [...prev, theme.name]
                          );
                          resetBulkPreparation();
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )}

            {bulkFailed.length > 0 && bulkPrepared && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {language === 'en'
                    ? `Could not prepare ${bulkFailed.length} student(s):`
                    : `Не удалось подготовить учеников: ${bulkFailed.length}`}
                </Typography>
                {bulkFailed.map((f, i) => (
                  <Typography key={i} variant="body2">
                    {f.name} — {f.message}
                  </Typography>
                ))}
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {bulkPrepared.length > 0
                    ? language === 'en'
                      ? 'You can retry, or continue without them.'
                      : 'Можно повторить или продолжить без них.'
                    : language === 'en'
                      ? 'No student could be prepared. Try again.'
                      : 'Не удалось подготовить ни одного ученика. Попробуйте ещё раз.'}
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setBulkOpen(false)}>
              {language === 'en' ? 'Cancel' : 'Отмена'}
            </Button>
            {bulkPrepared && bulkPrepared.length > 0 && !bulkLoading && (
              <Button
                onClick={() => launchBulkSession(bulkPrepared, bulkFailed)}
              >
                {language === 'en'
                  ? `Continue without them (${bulkPrepared.length})`
                  : `Продолжить без них (${bulkPrepared.length})`}
              </Button>
            )}
            <Button
              variant="contained"
              disableElevation
              disabled={
                !bulkTemplate ||
                bulkSelectedThemes.length === 0 ||
                bulkChosenCount === 0 ||
                bulkLoading
              }
              onClick={handleBulkStart}
              startIcon={
                bulkLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <BoltIcon />
                )
              }
              sx={{ fontWeight: 700, borderRadius: 2 }}
            >
              {bulkLoading
                ? language === 'en'
                  ? 'Preparing...'
                  : 'Подготовка...'
                : bulkPrepared
                  ? language === 'en'
                    ? 'Retry'
                    : 'Повторить'
                  : language === 'en'
                    ? `Start (${bulkChosenCount} student${bulkChosenCount === 1 ? '' : 's'})`
                    : `Начать (${ruStudentsCount(bulkChosenCount)})`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Bulk Assess: speed mode cycling ── */}
        {bulkQueue.length > 0 && bulkCurrentIdx < bulkQueue.length && (
          <FlashcardAssessment
            key={`bulk-${bulkCurrentIdx}`}
            open={bulkSpeedOpen}
            onClose={handleBulkClose}
            profile={bulkQueue[bulkCurrentIdx]?.profile}
            profileId={bulkQueue[bulkCurrentIdx]?.profile?._id}
            initialThemeFilter={
              bulkSelectedThemes.length > 0 ? bulkSelectedThemes : undefined
            }
            bulkSession={{
              assessed: bulkResults.filter((r) => !r.skipped).length,
              total: bulkQueue.length,
              isLast: bulkCurrentIdx === bulkQueue.length - 1,
              index: bulkCurrentIdx,
              studentName: bulkQueue[bulkCurrentIdx]?.student?.name,
              students: bulkQueue.map((q, i) => ({
                name: q.student?.name,
                status:
                  i < bulkResults.length
                    ? bulkResults[i].skipped
                      ? 'skipped'
                      : 'done'
                    : i === bulkCurrentIdx
                      ? 'current'
                      : 'pending',
              })),
            }}
            onSkipStudent={handleBulkSkip}
            onSave={handleBulkSave}
          />
        )}

        {/* ── Bulk Assess: results summary ── */}
        <Dialog
          open={bulkDone && bulkQueue.length > 0}
          onClose={handleBulkSummaryClose}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          {(() => {
            const assessed = bulkResults.filter((r) => !r.skipped);
            const skipped = bulkResults.filter((r) => r.skipped);
            const notReached = bulkQueue.slice(bulkResults.length);
            const pctOf = (r) =>
              r.maxScore > 0 ? Math.round((r.score / r.maxScore) * 100) : 0;
            const avg =
              assessed.length > 0
                ? Math.round(
                    assessed.reduce((s, r) => s + pctOf(r), 0) / assessed.length
                  )
                : null;
            const barColor = (pct) =>
              pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error';
            const cellSx = { px: 0.75, py: 0.75 };
            const nameList = (label, names, color) => (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color, lineHeight: 1.8 }}
                >
                  {label}
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                  {names.map((n, i) => (
                    <Chip key={i} label={n || '?'} size="small" />
                  ))}
                </Stack>
              </Box>
            );
            return (
              <>
                <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
                  {language === 'en' ? 'Session complete' : 'Сессия завершена'}
                  <Typography
                    variant="body2"
                    component="span"
                    color="text.secondary"
                    sx={{ fontWeight: 500, display: 'block' }}
                  >
                    {language === 'en'
                      ? `Assessed ${assessed.length} of ${bulkQueue.length}`
                      : `Оценено ${assessed.length} из ${bulkQueue.length}`}
                    {avg !== null &&
                      (language === 'en'
                        ? ` · average ${avg}%`
                        : ` · в среднем ${avg}%`)}
                  </Typography>
                </DialogTitle>
                <DialogContent>
                  {assessed.length > 0 && (
                    <Table size="small" sx={{ '& th, & td': cellSx }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {language === 'en' ? 'Student' : 'Ученик'}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: '#059669' }}
                          >
                            {language === 'en' ? 'Knows' : 'Знает'}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: '#d97706' }}
                          >
                            {language === 'en' ? 'Partly' : 'Частично'}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: '#dc2626' }}
                          >
                            {language === 'en' ? "Doesn't" : 'Не знает'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            %
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assessed.map((r, i) => {
                          const pct = pctOf(r);
                          return (
                            <TableRow key={i}>
                              <TableCell sx={{ width: '38%', maxWidth: 160 }}>
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{ fontWeight: 600 }}
                                >
                                  {r.name}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  color={barColor(pct)}
                                  sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                {r.known ?? '—'}
                              </TableCell>
                              <TableCell align="center">
                                {r.partial ?? '—'}
                              </TableCell>
                              <TableCell align="center">
                                {r.unknown ?? '—'}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 800,
                                  color: `${barColor(pct)}.main`,
                                }}
                              >
                                {pct}%
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                  {skipped.length > 0 &&
                    nameList(
                      language === 'en'
                        ? `Skipped — not in class (${skipped.length})`
                        : `Пропущены — нет на уроке (${skipped.length})`,
                      skipped.map((r) => r.name),
                      'text.secondary'
                    )}
                  {notReached.length > 0 &&
                    nameList(
                      language === 'en'
                        ? `Not assessed — session ended early (${notReached.length})`
                        : `Не оценены — сессия завершена досрочно (${notReached.length})`,
                      notReached.map((q) => q.student?.name),
                      'text.secondary'
                    )}
                  {bulkFailed.length > 0 &&
                    nameList(
                      language === 'en'
                        ? `Could not be prepared (${bulkFailed.length})`
                        : `Не удалось подготовить (${bulkFailed.length})`,
                      bulkFailed.map((f) => f.name),
                      'error.main'
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={handleBulkSummaryClose}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  >
                    {language === 'en' ? 'Done' : 'Готово'}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>
      </Container>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        // top: at the bottom it covered dialog buttons and the mobile nav
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
      <PdfExportPanel
        open={pdfPanelOpen}
        onClose={() => setPdfPanelOpen(false)}
        language={language}
        defaultMode="combined"
      />
      <Dialog
        open={Boolean(mergeSource)}
        onClose={() => !merging && setMergeSource(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {language === 'en'
            ? 'Merge student data'
            : 'Объединить данные ученика'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {language === 'en'
              ? `All data from "${mergeSource?.name}" (attendance, assessments, portfolio, notes) will be moved into the student you pick. "${mergeSource?.name}" will then be deleted. This cannot be undone.`
              : `Все данные «${mergeSource?.name}» (посещаемость, оценки, портфолио, заметки) будут перенесены выбранному ученику. После этого «${mergeSource?.name}» будет удалён. Действие необратимо.`}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>
              {language === 'en' ? 'Merge into…' : 'Объединить с…'}
            </InputLabel>
            <Select
              value={mergeTargetId}
              label={language === 'en' ? 'Merge into…' : 'Объединить с…'}
              onChange={(e) => setMergeTargetId(e.target.value)}
            >
              {students
                .filter((s) => s._id !== mergeSource?._id)
                .map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeSource(null)} disabled={merging}>
            {language === 'en' ? 'Cancel' : 'Отмена'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disableElevation
            disabled={merging || !mergeTargetId}
            startIcon={
              merging ? <CircularProgress size={16} /> : <CallMergeIcon />
            }
            onClick={doMerge}
          >
            {language === 'en' ? 'Merge & delete' : 'Объединить и удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
